import { z } from 'zod';
import { completeCampaignConfigSchema } from './campaignManagement';

const RESERVED_TWITCH_PACKAGE_FILENAMES = new Set([
  'component.html',
  'panel.html',
  'mobile.html',
  'mobile.js',
  'config.html',
  'config.js',
  'styles.css',
]);

const safeBasenameSchema = z
  .string()
  .min(1)
  .regex(/^[A-Za-z0-9][A-Za-z0-9._-]*$/, 'Filename must be a safe basename');

const twitchExtensionAssetFilenameSchema = safeBasenameSchema.refine(
  (filename) => !RESERVED_TWITCH_PACKAGE_FILENAMES.has(filename.toLowerCase()),
  'Asset filename must not replace a Twitch package runtime file'
);

const twitchExtensionZipFilenameSchema = safeBasenameSchema.regex(/\.zip$/i, 'zipFilename must end with .zip');

const optionalNonEmptyStringSchema = z.preprocess(
  (value) => value === '' ? undefined : value,
  z.string().min(1).optional()
);

const optionalUrlSchema = z.preprocess(
  (value) => value === '' ? undefined : value,
  z.string().url().optional()
);

export const twitchExtensionSurfaceSchema = z.enum(['Panel', 'Video - Component', 'Mobile']);

export const twitchExtensionAssetSourceSchema = z.enum([
  'campaign-media',
  'steam-store',
  'steam-cdn',
  'user-upload',
  'manual',
]);

export const twitchExtensionDraftAssetSchema = z.object({
  filename: twitchExtensionAssetFilenameSchema,
  source: twitchExtensionAssetSourceSchema,
  sourceUrl: z.string().url().optional(),
  localPath: z.string().min(1).optional(),
  contentType: z.string().min(1).optional(),
  warning: z.string().min(1).optional(),
});

export const twitchExtensionEditableDraftSchema = z.object({
  folderName: z.string().min(1),
  extensionName: z.string().min(1),
  title: z.string().min(1),
  eyebrow: z.string().min(1),
  bodyCopy: z.string().min(1),
  metaLine: z.string().min(1),
  releaseChip: z.string().min(1),
  developerChip: z.string().min(1),
  demoUrl: z.string().url(),
  steamAppId: optionalNonEmptyStringSchema,
  steamStoreUrl: optionalUrlSchema,
  iconAsset: twitchExtensionDraftAssetSchema,
  capsuleAsset: twitchExtensionDraftAssetSchema,
  zipFilename: twitchExtensionZipFilenameSchema,
});

export const twitchExtensionDraftWarningSchema = z.object({
  code: z.string().min(1),
  message: z.string().min(1),
  field: z.string().min(1).optional(),
});

export const twitchExtensionMetadataSchema = z.object({
  extensionName: z.string().min(1),
  folderName: z.string().min(1),
  build: z.object({
    zipFile: z.string().min(1),
  }),
  twitch: z.object({
    typeOfExtension: z.array(twitchExtensionSurfaceSchema).min(1),
    viewerPaths: z.object({
      component: z.string().min(1),
      panel: z.string().min(1),
      mobile: z.string().min(1),
      config: z.string().min(1),
    }),
    externalUrlAllowlist: z.array(z.string().url()).min(1),
    videoComponentSizing: z.object({
      targetHeight: z.string().min(1),
      aspectRatio: z.string().min(1),
      autoscale: z.boolean(),
      scalePixels: z.number().nullable(),
    }),
  }),
  playcast: z.object({
    demoUrl: z.string().url(),
  }),
  steam: z.object({
    appId: z.string().min(1).optional(),
    storeUrl: z.string().url().optional(),
  }),
  consoleAssets: z.object({
    logoImage: z.string().min(1),
    taskbarIcon: z.string().min(1),
    discoveryImage: z.string().min(1),
    screenshotImage: z.string().min(1),
  }),
});

export const twitchExtensionConsoleAssetsSchema = z.object({
  logoImage: z.string().min(1),
  taskbarIcon: z.string().min(1),
  discoveryImage: z.string().min(1),
  screenshotImage: z.string().min(1),
});

export const twitchExtensionDraftRequestSchema = z.object({
  campaignId: z.string().min(1).optional(),
  campaign: completeCampaignConfigSchema.optional(),
  overrides: twitchExtensionEditableDraftSchema.partial().optional(),
}).refine(
  ({ campaignId, campaign }) => Boolean(campaignId || campaign),
  {
    message: 'campaignId or campaign must be provided',
    path: ['campaignId'],
  }
);

export const twitchExtensionDraftResponseSchema = z.object({
  draft: twitchExtensionEditableDraftSchema,
  metadata: twitchExtensionMetadataSchema,
  warnings: z.array(twitchExtensionDraftWarningSchema),
});

export const twitchExtensionBuildRequestSchema = z.object({
  draft: twitchExtensionEditableDraftSchema,
});

export const twitchExtensionBuildResultSchema = z.object({
  zipFilename: twitchExtensionZipFilenameSchema,
  zipBytes: z.custom<Uint8Array>((value) => value instanceof Uint8Array, 'zipBytes must be Uint8Array'),
  sizeBytes: z.number().int().nonnegative(),
  metadata: twitchExtensionMetadataSchema,
  consoleAssets: twitchExtensionConsoleAssetsSchema,
});

export type TwitchExtensionSurface = z.infer<typeof twitchExtensionSurfaceSchema>;
export type TwitchExtensionAssetSource = z.infer<typeof twitchExtensionAssetSourceSchema>;
export type TwitchExtensionDraftAsset = z.infer<typeof twitchExtensionDraftAssetSchema>;
export type TwitchExtensionEditableDraft = z.infer<typeof twitchExtensionEditableDraftSchema>;
export type TwitchExtensionDraftWarning = z.infer<typeof twitchExtensionDraftWarningSchema>;
export type TwitchExtensionMetadata = z.infer<typeof twitchExtensionMetadataSchema>;
export type TwitchExtensionConsoleAssets = z.infer<typeof twitchExtensionConsoleAssetsSchema>;
export type TwitchExtensionDraftRequest = z.infer<typeof twitchExtensionDraftRequestSchema>;
export type TwitchExtensionDraftResponse = z.infer<typeof twitchExtensionDraftResponseSchema>;
export type TwitchExtensionBuildRequest = z.infer<typeof twitchExtensionBuildRequestSchema>;
export type TwitchExtensionBuildResult = z.infer<typeof twitchExtensionBuildResultSchema>;
